import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Instagram, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { school, whatsappLink } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const turmas = ["Berçário 1", "Berçário 2", "Maternal 1", "Maternal 2", "Pré 1", "Pré 2"];
const periodos = ["Manhã", "Tarde", "Integral"];

export default function Enrollment() {
  const [form, setForm] = useState({ nome_responsavel: "", nome_crianca: "", telefone: "", email: "", turma: "", periodo: "", mensagem: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setChip = (k, v) => setForm((f) => ({ ...f, [k]: f[k] === v ? "" : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome_responsavel || !form.telefone || !form.email) {
      toast.error("Preencha nome, telefone e e-mail.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enrollments`, form);
      setDone(true);
      toast.success("Recebemos seu interesse! Em breve entraremos em contato.");
    } catch (err) {
      toast.error("Não foi possível enviar. Tente novamente ou fale no WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full h-14 rounded-2xl border-2 border-ink/15 bg-white px-5 font-body text-ink placeholder:text-ink-soft/60 focus:outline-none focus:border-brand-sky focus:ring-4 focus:ring-brand-sky/15 transition-colors duration-200";

  return (
    <section id="matriculas" className="relative py-24 md:py-32 bg-surface-alt">
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Info side */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="inline-block rounded-full bg-brand-orange/15 text-brand-orange px-4 py-1.5 font-700 text-sm mb-4">
              Matrículas abertas
            </span>
            <h2 className="font-display font-700 text-ink text-4xl md:text-5xl leading-tight mb-5">
              Vamos conhecer a Recriar juntos?
            </h2>
            <p className="font-body text-ink-soft text-lg leading-relaxed mb-8">
              Preencha o formulário e nossa equipe entrará em contato para agendar uma visita.
              Prefere conversar agora? Fale direto com a gente.
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <a href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="enroll-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-green px-6 py-3.5 font-700 text-white shadow-[0_6px_20px_rgb(78,205,196,0.4)] hover:-translate-y-0.5 transition-transform duration-300">
                <MessageCircle className="h-5 w-5" strokeWidth={2.5} /> WhatsApp
              </a>
              <a href={school.instagramUrl} target="_blank" rel="noreferrer" data-testid="enroll-instagram-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-pink px-6 py-3.5 font-700 text-white shadow-[0_6px_20px_rgb(255,158,206,0.4)] hover:-translate-y-0.5 transition-transform duration-300">
                <Instagram className="h-5 w-5" strokeWidth={2.5} /> Instagram
              </a>
              <a href={`mailto:${school.email}`} data-testid="enroll-email-btn" className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-ink/15 px-6 py-3.5 font-700 text-ink hover:-translate-y-0.5 transition-transform duration-300">
                <Mail className="h-5 w-5" strokeWidth={2.5} /> E-mail
              </a>
            </div>

            <div className="space-y-4">
              {[
                { icon: MapPin, text: school.address },
                { icon: Clock, text: school.hours },
              ].map((it, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-sky/15 shrink-0">
                    <it.icon className="h-5 w-5 text-brand-sky" strokeWidth={2.5} />
                  </span>
                  <span className="font-body font-600 text-ink">{it.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Form side */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="rounded-[2rem] bg-white p-7 md:p-10 shadow-[0_20px_60px_rgb(30,58,95,0.1)]">
            {done ? (
              <div className="flex flex-col items-center text-center py-12" data-testid="enroll-success">
                <CheckCircle2 className="h-16 w-16 text-brand-green mb-5" strokeWidth={2} />
                <h3 className="font-display font-700 text-ink text-2xl mb-2">Interesse enviado!</h3>
                <p className="font-body text-ink-soft mb-6">Obrigado, {form.nome_responsavel.split(" ")[0]}. Entraremos em contato em breve.</p>
                <a href={whatsappLink(`Olá! Acabei de enviar o formulário no site. Sou ${form.nome_responsavel}.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-brand-green px-6 py-3 font-700 text-white">
                  <MessageCircle className="h-5 w-5" /> Adiantar pelo WhatsApp
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="enrollment-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="input-responsavel" className={inputCls} placeholder="Seu nome *" value={form.nome_responsavel} onChange={update("nome_responsavel")} />
                  <input data-testid="input-crianca" className={inputCls} placeholder="Nome da criança" value={form.nome_crianca} onChange={update("nome_crianca")} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="input-telefone" className={inputCls} placeholder="WhatsApp / Telefone *" value={form.telefone} onChange={update("telefone")} />
                  <input data-testid="input-email" type="email" className={inputCls} placeholder="E-mail *" value={form.email} onChange={update("email")} />
                </div>

                <div>
                  <p className="font-body font-600 text-ink-soft text-sm mb-2">Turma de interesse</p>
                  <div className="flex flex-wrap gap-2">
                    {turmas.map((t) => (
                      <button key={t} type="button" onClick={() => setChip("turma", t)} data-testid={`chip-turma-${t}`} className={`rounded-full px-4 py-2 font-600 text-sm border-2 transition-colors duration-200 ${form.turma === t ? "bg-brand-sky border-brand-sky text-white" : "bg-white border-ink/15 text-ink-soft hover:border-brand-sky"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="font-body font-600 text-ink-soft text-sm mb-2">Período</p>
                  <div className="flex flex-wrap gap-2">
                    {periodos.map((p) => (
                      <button key={p} type="button" onClick={() => setChip("periodo", p)} data-testid={`chip-periodo-${p}`} className={`rounded-full px-4 py-2 font-600 text-sm border-2 transition-colors duration-200 ${form.periodo === p ? "bg-brand-orange border-brand-orange text-white" : "bg-white border-ink/15 text-ink-soft hover:border-brand-orange"}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <textarea data-testid="input-mensagem" rows={3} className="w-full rounded-2xl border-2 border-ink/15 bg-white px-5 py-4 font-body text-ink placeholder:text-ink-soft/60 focus:outline-none focus:border-brand-sky focus:ring-4 focus:ring-brand-sky/15 transition-colors duration-200 resize-none" placeholder="Mensagem (opcional)" value={form.mensagem} onChange={update("mensagem")} />

                <button type="submit" disabled={loading} data-testid="enrollment-submit-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-8 py-4 font-700 text-white text-lg shadow-[0_10px_30px_rgb(255,159,28,0.4)] hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgb(255,159,28,0.5)] transition-transform duration-300 disabled:opacity-60 disabled:hover:translate-y-0">
                  {loading ? "Enviando..." : (<><Send className="h-5 w-5" strokeWidth={2.5} /> Quero saber mais</>)}
                </button>
                <p className="text-center font-body text-ink-soft/70 text-xs">Ao enviar, você concorda em ser contatado pela equipe da Recriar.</p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
